import { useCallback } from 'react';
import { useUpgrade } from '../contexts/UpgradeContext';

interface LimitErrorResponse {
  success: false;
  message: string;
  limit?: {
    type: 'staff' | 'projects' | 'billExtractions' | 'integrations';
    max: number;
    current: number;
    remaining: number;
  };
}

/**
 * Hook to handle API errors and automatically trigger upgrade flow when hitting free tier limits
 * 
 * Usage:
 * ```
 * const { handleApiError, openUpgradeFlow } = useUpgradeFlow();
 * 
 * // Option 1: Check response automatically
 * const response = await fetch(...);
 * const data = await response.json();
 * const wasLimitError = handleApiError(response, data);
 * if (wasLimitError) return; // Upgrade modal will be shown
 * 
 * // Option 2: Manually trigger upgrade flow
 * openUpgradeFlow({ limitType: 'staff' });
 * ```
 */
export const useUpgradeFlow = () => {
  const { openUpgradeModal } = useUpgrade();

  /**
   * Manually open the upgrade modal with optional context
   */
  const openUpgradeFlow = useCallback((options?: {
    returnPath?: string;
    limitType?: 'staff' | 'projects' | 'billExtractions' | 'integrations';
    limitDetails?: { max?: number; current?: number; remaining?: number };
  }) => {
    openUpgradeModal(options || {});
  }, [openUpgradeModal]);

  /**
   * Check if an API response is a limit error and automatically trigger upgrade flow
   * Returns true if it was a limit error, false otherwise
   */
  const handleApiError = useCallback((response: Response, data: any): boolean => {
    // Check if it's a 403 with limit information
    if (response.status === 403 && data && !data.success) {
      // Check if the error message contains limit-related keywords
      const message = data.message || '';
      const isLimitError = 
        message.toLowerCase().includes('limit') || 
        message.toLowerCase().includes('upgrade') ||
        message.toLowerCase().includes('premium');

      if (isLimitError && data.limit) {
        // Parse limit information from response
        openUpgradeModal({
          limitType: data.limit.type,
          limitDetails: {
            max: data.limit.max,
            current: data.limit.current,
            remaining: data.limit.remaining
          }
        });
        return true;
      }

      // Fallback: detect limit type from message
      if (isLimitError) {
        let limitType: 'staff' | 'projects' | 'billExtractions' | 'integrations' | undefined;
        
        if (message.toLowerCase().includes('staff')) {
          limitType = 'staff';
        } else if (message.toLowerCase().includes('project')) {
          limitType = 'projects';
        } else if (message.toLowerCase().includes('bill') || message.toLowerCase().includes('extraction')) {
          limitType = 'billExtractions';
        } else if (message.toLowerCase().includes('integration')) {
          limitType = 'integrations';
        }

        openUpgradeModal({ limitType });
        return true;
      }
    }

    return false;
  }, [openUpgradeModal]);

  /**
   * Wrapper for fetch that automatically handles limit errors
   * Returns null if a limit error was encountered (upgrade modal shown)
   */
  const fetchWithUpgradeCheck = useCallback(async (
    url: string, 
    options?: RequestInit
  ): Promise<{ response: Response; data: any } | null> => {
    const response = await fetch(url, options);
    const data = await response.json();
    
    const wasLimitError = handleApiError(response, data);
    if (wasLimitError) {
      return null;
    }

    return { response, data };
  }, [handleApiError]);

  return {
    openUpgradeFlow,
    handleApiError,
    fetchWithUpgradeCheck
  };
};

